import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Download,
  Italic,
  Merge,
  PanelLeft,
  PanelTop,
  PilcrowLeft,
  PilcrowRight,
  Split,
  Type,
  Upload,
  WandSparkles,
} from "lucide-react";
import {
  ColumnInsertLeftSVG,
  ColumnInsertRightSVG,
  ColumnRemoveSVG,
  FreezeColumnSVG,
  FreezeRowSVG,
  RowInsertBottomSVG,
  RowInsertTopSVG,
  RowRemoveSVG,
} from "@/components/ui/SVG";
import { TABLE_MAX_COLS, TABLE_MAX_ROWS } from "@/cms/blocks/table";
import { getColumnCount, getSmartColumnWidths } from "./model";
import { type CellAlign, type CellDirection, type Coord, type TableCellModel } from "./types";
import { ControlButton, ToolbarGroup } from "./TableToolbar";

type TableEditorToolbarProps = {
  canMergeOrSplitSelection: boolean;
  columnWidths: number[];
  fixedCol?: number;
  fixedRow?: number;
  hasSelection: boolean;
  onAddColumn: (side: "before" | "after") => void;
  onAddRow: (side: "before" | "after") => void;
  onDeleteColumn: () => void;
  onDeleteRow: () => void;
  onExportExcel: () => void;
  onFixColumn: () => void;
  onFixRow: () => void;
  onImportExcel: () => void;
  onMergeOrSplit: () => void;
  onSetAlign: (align: CellAlign) => void;
  onSetColumnWidths: (widths: number[]) => void;
  onSetDirection: (direction: CellDirection) => void;
  onToggleBold: () => void;
  onToggleHeaderCell: () => void;
  onToggleHeaderColumn: () => void;
  onToggleHeaderRow: () => void;
  onToggleItalic: () => void;
  selectedAnchor: Coord;
  selectedCell?: TableCellModel;
  shouldSplitSelection: boolean;
  table: TableCellModel[][];
};

export function TableEditorToolbar({
  canMergeOrSplitSelection,
  fixedCol,
  fixedRow,
  hasSelection,
  onAddColumn,
  onAddRow,
  onDeleteColumn,
  onDeleteRow,
  onExportExcel,
  onFixColumn,
  onFixRow,
  onImportExcel,
  onMergeOrSplit,
  onSetAlign,
  onSetColumnWidths,
  onSetDirection,
  onToggleBold,
  onToggleHeaderCell,
  onToggleHeaderColumn,
  onToggleHeaderRow,
  onToggleItalic,
  selectedAnchor,
  selectedCell,
  shouldSplitSelection,
  table,
}: TableEditorToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-y border-gray-200 bg-slate-50 px-3 py-2">
      <ToolbarGroup title="Columns">
        <ControlButton title="Add column left" icon={<ColumnInsertLeftSVG className="h-5 w-5" />} disabled={!hasSelection || getColumnCount(table) >= TABLE_MAX_COLS} onClick={() => onAddColumn("before")} />
        <ControlButton title="Add column right" icon={<ColumnInsertRightSVG className="h-5 w-5" />} disabled={!hasSelection || getColumnCount(table) >= TABLE_MAX_COLS} onClick={() => onAddColumn("after")} />
        <ControlButton title="Delete column" icon={<ColumnRemoveSVG className="h-5 w-5" />} disabled={!hasSelection || getColumnCount(table) <= 1} onClick={onDeleteColumn} danger />
        <ControlButton title="Smart column widths" icon={<WandSparkles size={16} />} onClick={() => onSetColumnWidths(getSmartColumnWidths(table))} />
      </ToolbarGroup>
      <ToolbarGroup title="Rows">
        <ControlButton title="Add row above" icon={<RowInsertTopSVG className="h-5 w-5" />} disabled={!hasSelection || table.length >= TABLE_MAX_ROWS} onClick={() => onAddRow("before")} />
        <ControlButton title="Add row below" icon={<RowInsertBottomSVG className="h-5 w-5" />} disabled={!hasSelection || table.length >= TABLE_MAX_ROWS} onClick={() => onAddRow("after")} />
        <ControlButton title="Delete row" icon={<RowRemoveSVG className="h-5 w-5" />} disabled={!hasSelection || table.length <= 1} onClick={onDeleteRow} danger />
      </ToolbarGroup>
      <ToolbarGroup title="Cells">
        <ControlButton
          title={shouldSplitSelection ? "Split cell" : "Merge cells"}
          icon={shouldSplitSelection ? <Split size={17} /> : <Merge size={17} />}
          disabled={!canMergeOrSplitSelection}
          onClick={onMergeOrSplit}
        />
      </ToolbarGroup>
      <ToolbarGroup title="Typography">
        <ControlButton title="Bold" icon={<Bold size={16} />} disabled={!hasSelection} active={Boolean(selectedCell?.bold)} onClick={onToggleBold} />
        <ControlButton title="Italic" icon={<Italic size={16} />} disabled={!hasSelection} active={Boolean(selectedCell?.italic)} onClick={onToggleItalic} />
        <ControlButton title="Align left" icon={<AlignLeft size={17} />} disabled={!hasSelection} active={(selectedCell?.align ?? "left") === "left"} onClick={() => onSetAlign("left")} />
        <ControlButton title="Align center" icon={<AlignCenter size={17} />} disabled={!hasSelection} active={selectedCell?.align === "center"} onClick={() => onSetAlign("center")} />
        <ControlButton title="Align right" icon={<AlignRight size={17} />} disabled={!hasSelection} active={selectedCell?.align === "right"} onClick={() => onSetAlign("right")} />
        <ControlButton title="Left-to-right text" icon={<PilcrowRight size={17} />} disabled={!hasSelection} active={(selectedCell?.direction ?? "ltr") === "ltr"} onClick={() => onSetDirection("ltr")} />
        <ControlButton title="Right-to-left text" icon={<PilcrowLeft size={17} />} disabled={!hasSelection} active={selectedCell?.direction === "rtl"} onClick={() => onSetDirection("rtl")} />
      </ToolbarGroup>
      <ToolbarGroup title="Headers">
        <ControlButton title="Toggle header row" icon={<PanelTop size={17} />} disabled={!hasSelection} onClick={onToggleHeaderRow} />
        <ControlButton title="Toggle header column" icon={<PanelLeft size={17} />} disabled={!hasSelection} onClick={onToggleHeaderColumn} />
        <ControlButton title="Toggle header cell" icon={<Type size={16} />} disabled={!hasSelection} onClick={onToggleHeaderCell} />
      </ToolbarGroup>
      <ToolbarGroup title="Fixed">
        <ControlButton title={fixedRow === selectedAnchor.row ? "Unfix rows" : "Fix rows above selection"} icon={<FreezeRowSVG className="h-5 w-5" />} disabled={!hasSelection || selectedAnchor.row === 0} active={fixedRow === selectedAnchor.row} onClick={onFixRow} />
        <ControlButton title={fixedCol === selectedAnchor.col ? "Unfix columns" : "Fix columns before selection"} icon={<FreezeColumnSVG className="h-5 w-5" />} disabled={!hasSelection || selectedAnchor.col === 0} active={fixedCol === selectedAnchor.col} onClick={onFixColumn} />
      </ToolbarGroup>
      <ToolbarGroup title="Excel">
        <ControlButton title="Import Excel file" icon={<Upload size={16} />} onClick={onImportExcel} />
        <ControlButton title="Export Excel file" icon={<Download size={16} />} onClick={onExportExcel} />
      </ToolbarGroup>
    </div>
  );
}
